

import React, { useState, useEffect } from 'react';
import {
    Modal,
    Box,
    Grid,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    IconButton,
    Snackbar,
    Alert,
    Autocomplete,
} from '@mui/material';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from 'auth/Auth';
import colors from 'assets/theme/base/colors';

const NewTravelInvoiceLineItemModal = ({ isOpen, onClose, onSave }) => {
    const { axiosInstance, user } = useAuth();
    const [isSaving, setIsSaving] = useState(false);
    const [travelInvoices, setTravelInvoices] = useState([]);
    const [travelOrders, setTravelOrders] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [formData, setFormData] = useState({
        name: '',
        invoice_id: null,
        order_id: null,
        customer_id: null,
        amount: '',
        currency: 'GBP',
        status: 'Draft',
        invoice_date: new Date().toISOString().split('T')[0],
        due_date: '',
        ref_no: '',
        customer_address: '',
        payment_template_id: null,
        term_snapshot: '',
        merchant_order_line_id: null,
    });

    const [formErrors, setFormErrors] = useState({});
    const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });

    useEffect(() => {
        if (isOpen) {
            fetchTravelInvoices();
            fetchTravelOrders();
            fetchAccounts();
        }
    }, [isOpen]);

    const fetchTravelInvoices = async () => {
        try {
            const response = await axiosInstance.get('/travel-invoices/');
            setTravelInvoices(response.data.map(inv => ({ value: inv.id, label: inv.name || `Invoice ${inv.id}` })));
        } catch (error) {
            console.error('Error fetching travel invoices:', error);
        }
    };

    const fetchTravelOrders = async () => {
        try {
            const response = await axiosInstance.get('/travel-orders/');
            setTravelOrders(response.data.map(order => ({ value: order.id, label: order.name || `Order ${order.id}` })));
        } catch (error) {
            console.error('Error fetching travel orders:', error);
        }
    };

    const fetchAccounts = async () => {
        try {
            const response = await axiosInstance.get('/accounts/');
            setAccounts(response.data.map(acc => ({ value: acc.id, label: acc.name || `Account ${acc.id}` })));
        } catch (error) {
            console.error('Error fetching accounts:', error);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        setFormErrors(prev => ({ ...prev, [name]: '' }));
    };

    const validateForm = () => {
        const errors = {};
        if (!formData.invoice_id) errors.invoice_id = 'Invoice is required';
        if (!formData.order_id) errors.order_id = 'Order is required';
        if (!formData.amount) errors.amount = 'Amount is required';
        if (!formData.invoice_date) errors.invoice_date = 'Invoice Date is required';
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setIsSaving(true);
        try {
            const payload = {
                ...formData,
                amount: parseFloat(formData.amount),
                created_by: user?.id,
                updated_by: user?.id
            };

            await onSave(payload);
            onClose();
            setFormData({
                name: '',
                invoice_id: null,
                order_id: null,
                customer_id: null,
                amount: '',
                currency: 'GBP',
                status: 'Draft',
                invoice_date: new Date().toISOString().split('T')[0],
                due_date: '',
                ref_no: '',
                customer_address: '',
                payment_template_id: null,
                term_snapshot: '',
                merchant_order_line_id: null,
            });
        } catch (error) {
            console.error('Error saving Travel Invoice Line Item:', error);
            let errorMessage = 'Error saving Travel Invoice Line Item';
            
            if (error.response?.data) {
                if (typeof error.response.data.detail === 'string') {
                    errorMessage = error.response.data.detail;
                } else if (Array.isArray(error.response.data.detail)) {
                    errorMessage = error.response.data.detail.map(err => 
                        typeof err === 'string' ? err : err.msg || JSON.stringify(err)
                    ).join(', ');
                } else if (typeof error.response.data.detail === 'object') {
                    errorMessage = error.response.data.detail.msg || JSON.stringify(error.response.data.detail);
                }
            }
            
            setToast({
                open: true,
                message: errorMessage,
                severity: 'error'
            });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <>
            <Modal open={isOpen} onClose={onClose}>
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: { xs: '95%', sm: '80%', md: '700px' },
                    maxHeight: '90vh',
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    borderRadius: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                }}>
                    <Box className="pearl-popup-header" sx={{
                        backgroundColor: colors.popupHeader.background,
                        px: 3,
                        py: 2,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <MDTypography variant="h6" color="white">Add New Travel Invoice Line Item</MDTypography>
                        <IconButton onClick={onClose} size="small" sx={{ color: 'white' }}>
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    <Box sx={{ p: 4, overflowY: 'auto' }}>
                        <form onSubmit={handleSubmit}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <Autocomplete
                                        options={travelInvoices}
                                        getOptionLabel={(option) => option?.label || ''}
                                        value={travelInvoices.find(inv => inv.value === formData.invoice_id) || null}
                                        onChange={(e, newValue) => {
                                            setFormData(prev => ({ ...prev, invoice_id: newValue?.value || null }));
                                        }}
                                        sx={{ '& .MuiOutlinedInput-root': { height: '35px' } }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Invoice"
                                                required
                                                error={!!formErrors.invoice_id}
                                                helperText={typeof formErrors.invoice_id === 'string' ? formErrors.invoice_id : ''}
                                                variant="outlined"
                                                margin="normal"
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Autocomplete
                                        options={travelOrders}
                                        getOptionLabel={(option) => option?.label || ''}
                                        value={travelOrders.find(order => order.value === formData.order_id) || null}
                                        onChange={(e, newValue) => {
                                            setFormData(prev => ({ ...prev, order_id: newValue?.value || null }));
                                        }}
                                        sx={{ '& .MuiOutlinedInput-root': { height: '35px' } }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Order"
                                                required
                                                error={!!formErrors.order_id}
                                                helperText={typeof formErrors.order_id === 'string' ? formErrors.order_id : ''}
                                                variant="outlined"
                                                margin="normal"
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Autocomplete
                                        options={accounts}
                                        getOptionLabel={(option) => option?.label || ''}
                                        value={accounts.find(acc => acc.value === formData.customer_id) || null}
                                        onChange={(e, newValue) => {
                                            setFormData(prev => ({ ...prev, customer_id: newValue?.value || null }));
                                        }}
                                        sx={{ '& .MuiOutlinedInput-root': { height: '35px' } }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Customer"
                                                variant="outlined"
                                                margin="normal"
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Amount"
                                        name="amount"
                                        type="number"
                                        value={formData.amount}
                                        onChange={handleInputChange}
                                        error={!!formErrors.amount}
                                        helperText={typeof formErrors.amount === 'string' ? formErrors.amount : ''}
                                        required
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal">
                                        <InputLabel>Currency</InputLabel>
                                        <Select
                                            name="currency"
                                            value={formData.currency}
                                            onChange={handleInputChange}
                                            label="Currency"

                                        >
                                            <MenuItem value="GBP">GBP</MenuItem>
                                            <MenuItem value="AUD">AUD</MenuItem>
                                            <MenuItem value="USD">USD</MenuItem>
                                            <MenuItem value="EUR">EUR</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Invoice Date"
                                        name="invoice_date"
                                        type="date"
                                        value={formData.invoice_date}
                                        onChange={handleInputChange}
                                        error={!!formErrors.invoice_date}
                                        helperText={typeof formErrors.invoice_date === 'string' ? formErrors.invoice_date : ''}
                                        required
                                        variant="outlined"
                                        margin="normal"
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Due Date"
                                        name="due_date"
                                        type="date"
                                        value={formData.due_date}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal">
                                        <InputLabel>Status</InputLabel>
                                        <Select
                                            name="status"
                                            value={formData.status}
                                            onChange={handleInputChange}
                                            label="Status"

                                        >
                                            <MenuItem value="Draft">Draft</MenuItem>
                                            <MenuItem value="Issued">Issued</MenuItem>
                                            <MenuItem value="PartiallyPaid">Partially Paid</MenuItem>
                                            <MenuItem value="Paid">Paid</MenuItem>
                                            <MenuItem value="Cancelled">Cancelled</MenuItem>
                                            <MenuItem value="Refunded">Refunded</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
                                <MDButton onClick={onClose} variant="outlined" color="secondary">
                                    Cancel
                                </MDButton>
                                <MDButton type="submit" variant="gradient" color="info" disabled={isSaving}>
                                    {isSaving ? 'Saving...' : 'Save'}
                                </MDButton>
                            </Box>
                        </form>
                    </Box>
                </Box>
            </Modal>
            <Snackbar open={toast.open} autoHideDuration={6000} onClose={() => setToast({ ...toast, open: false })}>
                <Alert severity={toast.severity} onClose={() => setToast({ ...toast, open: false })}>
                    {toast.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default NewTravelInvoiceLineItemModal;

